import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { IonNav, AlertController } from '@ionic/angular';
import * as moment from 'moment';
import { CommonService } from 'src/app/services/common.service';
import { CaseService } from 'src/app/services/case.service';
import { AuthService } from 'src/app/services/auth.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-ezidebit-recurring-card',
  templateUrl: './ezidebit-card.page.html',
  styleUrls: ['./ezidebit-card.page.scss'],
})
export class EzidebitRecurringCardPage implements OnInit {
  @Input() caseData;
  @Input() planType = 'plan';
  @Input() selectedPlanData;
  @Input() isPaymentProposal;

  caseId;
  debtorDetails = this.authService.debtorDetails;
  addCardForm: FormGroup;
  years: number[] = this.generateYearList();
  months: number[] = this.generateMonthList();
  caseList = [];
  saveBtnDisabled = false;
  surcharge_amount = '';
  paymentFrequencyOption = '';
  amount;
  frequency;
  firstPaymentDate;
  outstanding;
  showOption = false;

  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private caseService: CaseService,
    private authService: AuthService,
    private ionNav: IonNav,
    private alertController: AlertController,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    const paymentData = this.caseService.paymentData;
    this.amount = paymentData.amount;
    this.frequency = paymentData.freq;
    this.paymentFrequencyOption = paymentData.paymentFrequencyOption;
    this.firstPaymentDate = paymentData.date;
    this.outstanding = paymentData.total;
    this.caseList = paymentData.caseList;
    if (this.planType === 'group_plan') {
      this.caseId = this.caseData[0].id;
    } else {
      this.caseId = paymentData.caseId;
    }
    this.initForm();
  }

  generateYearList(): number[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 16 }, (_, i) => currentYear + i);
  }

  generateMonthList(): number[] {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }

  initForm() {
    this.addCardForm = this.formBuilder.group({
      card_number: ['', [Validators.required, Validators.pattern('^[0-9]{12,20}$')]],
      expiry_month: ['', [Validators.required]],
      expiry_year: ['', [Validators.required]],
      cvv: ['', [Validators.required]],
      debtor_name: [
        this.debtorDetails?.debtor_name || '',
        [Validators.required],
      ],
      debtor_email: [
        this.debtorDetails?.debtor_email || '',
        [Validators.required],
      ],
      address_ln1: [
        this.debtorDetails?.addresses[0]?.address_ln1 || '',
        [Validators.required],
      ],
      address_ln2: [this.debtorDetails?.addresses[0]?.address_ln2 || '', []],
      address_ln3: [this.debtorDetails?.addresses[0]?.address_ln3 || '', []],
      address_town: [this.debtorDetails?.addresses[0]?.address_town || '', []],
      address_postcode: [
        this.debtorDetails?.addresses[0]?.address_postcode || '',
        [Validators.required],
      ],
    });
  }

  async save() {
    this.addCardForm.markAllAsTouched();
    if (this.addCardForm.valid) {
      this.confirmPayment();
    }
  }

  async showAlert(message: string) {
    const alert = await this.alertController.create({
      header: this.translate.instant('PAYMENT.ALERT'),
      message,
      buttons: ['OK'],
    });
    await alert.present();
  }

  confirmPayment() {
    let start = '';
    if (this.paymentFrequencyOption === 'month') {
      start = moment(this.firstPaymentDate).format('YYYY-MM-DD');
    } else {
      start = this.firstPaymentDate.split('T')[0];
    }
    this.commonService.showLoader();
    let paymentPlan = this.caseService.getPaymentData();
    let data = {
      amount: this.amount ? this.amount : this.outstanding,
      freq: this.frequency,
      start: start,
      ref_amount: this.outstanding,
      surcharge_amount: this.surcharge_amount,
      card_number: this.addCardForm.value.card_number,
      cvv: this.addCardForm.value.cvv,
      expiry_month: this.addCardForm.value.expiry_month,
      expiry_year: this.addCardForm.value.expiry_year,
      debtor_name: this.addCardForm.value.debtor_name,
      debtor_email: this.addCardForm.value.debtor_email,
      address_ln1: this.addCardForm.value.address_ln1,
      address_ln2: this.addCardForm.value.address_ln2,
      address_ln3: this.addCardForm.value.address_ln3,
      town: this.addCardForm.value.address_town,
      address_postcode: this.addCardForm.value.address_postcode,
      debtor_id: this.debtorDetails.debtor_id,
      mode: 'make',
      payment_method: 'ezidebit',
      is_group: this.planType === 'group_plan' ? 1 : 0,
      cases: this.caseList,
      caseid: paymentPlan['isPaymentProposal'] ? this.caseId : undefined,
      frequency: paymentPlan['isPaymentProposal'] ? this.frequency : undefined,
      isGroupArrangement: paymentPlan['isPaymentProposal']
        ? this.planType === 'group_plan'
        : undefined,
      arrangement_id: undefined,
    };
    this.caseService
      .newEzidebitRecurringCardPayment(this.caseId, data)
      .subscribe((res: any) => {
        this.saveBtnDisabled = false;
        this.commonService.dismissLoader();
        if (res.data?.success) {
          this.caseService.saveCaseDetails(localStorage.getItem('debtor_id'));
          if (res.data.data?.arrangement_id) {
            data = { ...data, arrangement_id: res.data.data.arrangement_id };
          }
          this.caseService.paymentPlan(data).subscribe(() => {});
          this.ionNav.popToRoot();
          const successMessage =
            this.translate.instant('PAYMENT.ARRANGEMENT_CREATED_SUCCESSFULLY');
          this.commonService.showToast(
            successMessage !== 'PAYMENT.ARRANGEMENT_CREATED_SUCCESSFULLY'
              ? successMessage
              : 'Arrangement created successfully',
            'success'
          );
        } else {
          this.commonService.showToast(res.data?.message || res.message, 'danger');
        }
      });
  }

  toggleOptions(value) {
    this.showOption = value === false ? true : false;
  }
}
